import { apiClient } from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SalonBrand {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
  isActive: boolean;
  createdAt: string;
  salons?: BranchSalon[];
  _count?: { salons: number; products: number };
}

export interface BranchSalon {
  id: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  warehouse?: { id: string; _count: { items: number } };
  _count?: { staff: number; services: number };
}

export interface Warehouse {
  id: string;
  name: string;
  type: 'BRANCH' | 'CENTRAL';
  isActive: boolean;
  salonId?: string;
  brandId?: string;
  salon?: { id: string; name: string; city: string };
  brand?: { id: string; name: string };
  _count?: { items: number };
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  unit: string;
  category?: string;
  sellingPrice?: number;
  purchasePrice?: number;
  isActive: boolean;
  brandId?: string;
  salonId?: string;
  serviceConsumptions?: Array<{
    id: string;
    quantity: number;
    perClient?: number;
    otherParams?: {
      perMl?: number;
      clientsCount?: number;
      note?: string;
    };
    service: { id: string; name: string };
  }>;
  _count?: { inventoryItems: number };
}

export interface InventoryItem {
  id: string;
  quantity: number;
  minQuantity: number;
  product: Product;
  warehouseId: string;
}

export interface InventoryTransaction {
  id: string;
  type: string;
  quantity: number;
  notes?: string;
  createdAt: string;
  product: { id: string; name: string; unit: string };
  createdBy?: { id: string; email: string };
}

export interface StockTransfer {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'COMPLETED' | 'CANCELLED';
  notes?: string;
  requestedAt: string;
  completedAt?: string;
  fromWarehouse: { id: string; name: string; type: string };
  toWarehouse: { id: string; name: string; type: string };
  requestedBy?: { id: string; email: string };
  items: Array<{ id: string; requestedQty: number; sentQty?: number; product: { id: string; name: string; unit: string } }>;
}

export interface InventoryAudit {
  id: string;
  name: string;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  startedAt: string;
  completedAt?: string;
  notes?: string;
  warehouseId: string;
  createdBy?: { id: string; email: string };
  _count?: { items: number };
  items?: Array<{
    id: string;
    expectedQty: number;
    countedQty: number;
    difference: number;
    product: Product;
  }>;
}

// ─── Branch / Brand API ───────────────────────────────────────────────────────

export const branchService = {
  getBrands: () => apiClient.get<{ data: SalonBrand[] }>('/branches/brands').then(r => r.data.data),
  getBrand: (id: string) => apiClient.get<{ data: SalonBrand }>(`/branches/brands/${id}`).then(r => r.data.data),
  createBrand: (data: Partial<SalonBrand>) => apiClient.post<{ data: SalonBrand }>('/branches/brands', data).then(r => r.data.data),
  updateBrand: (id: string, data: Partial<SalonBrand>) => apiClient.put<{ data: SalonBrand }>(`/branches/brands/${id}`, data).then(r => r.data.data),
  deleteBrand: (id: string) => apiClient.delete(`/branches/brands/${id}`),
  createBranch: (brandId: string, data: Partial<BranchSalon>) => apiClient.post(`/branches/brands/${brandId}/branches`, data).then(r => r.data.data),
  linkSalon: (brandId: string, salonId: string) => apiClient.post(`/branches/brands/${brandId}/link`, { salonId }).then(r => r.data.data),
  unlinkSalon: (salonId: string) => apiClient.delete(`/branches/salons/${salonId}/unlink`),
  getCentralWarehouse: (brandId: string) => apiClient.post<{ data: Warehouse }>(`/branches/brands/${brandId}/central-warehouse`).then(r => r.data.data),
};

// ─── Inventory API ────────────────────────────────────────────────────────────

export const inventoryApiService = {
  // Products
  // `includeBrand` dokłada produkty marki, do ktorej nalezy salon — tyle, ile
  // dopuszcza walidacja sprzedazy. Uzywane przez kase i finalizacje wizyty.
  getProducts: (params: { salonId?: string; brandId?: string; includeBrand?: boolean }) =>
    apiClient.get<{ data: Product[] }>('/inventory/products', { params }).then(r => r.data.data),
  getProduct: (id: string) => apiClient.get<{ data: Product }>(`/inventory/products/${id}`).then(r => r.data.data),
  createProduct: (data: Partial<Product> & { salonId?: string; brandId?: string }) =>
    apiClient.post<{ data: Product }>('/inventory/products', data).then(r => r.data.data),
  updateProduct: (id: string, data: Partial<Product>) =>
    apiClient.put<{ data: Product }>(`/inventory/products/${id}`, data).then(r => r.data.data),
  deleteProduct: (id: string) => apiClient.delete(`/inventory/products/${id}`),

  // Warehouses
  getWarehousesForBrand: (brandId: string) =>
    apiClient.get<{ data: Warehouse[] }>(`/inventory/brands/${brandId}/warehouses`).then(r => r.data.data),
  getWarehousesForSalon: (salonId: string) =>
    apiClient.get<{ data: Warehouse[] }>(`/inventory/salons/${salonId}/warehouses`).then(r => r.data.data),
  getWarehouseStock: (warehouseId: string) =>
    apiClient.get<{ data: InventoryItem[] }>(`/inventory/warehouses/${warehouseId}/stock`).then(r => r.data.data),
  getTransactions: (warehouseId: string, limit?: number) =>
    apiClient.get<{ data: InventoryTransaction[] }>(`/inventory/warehouses/${warehouseId}/transactions`, { params: { limit } }).then(r => r.data.data),
  purchaseStock: (warehouseId: string, data: { productId: string; quantity: number; notes?: string }) =>
    apiClient.post(`/inventory/warehouses/${warehouseId}/purchase`, data).then(r => r.data.data),
  adjustStock: (warehouseId: string, data: { productId: string; quantity: number; notes?: string }) =>
    apiClient.post(`/inventory/warehouses/${warehouseId}/adjust`, data).then(r => r.data.data),

  // Transfers
  getTransfers: (params: { fromWarehouseId?: string; toWarehouseId?: string; brandId?: string }) =>
    apiClient.get<{ data: StockTransfer[] }>('/inventory/transfers', { params }).then(r => r.data.data),
  createTransfer: (data: { fromWarehouseId: string; toWarehouseId: string; notes?: string; items: Array<{ productId: string; requestedQty: number }> }) =>
    apiClient.post<{ data: StockTransfer }>('/inventory/transfers', data).then(r => r.data.data),
  completeTransfer: (transferId: string) =>
    apiClient.post<{ data: StockTransfer }>(`/inventory/transfers/${transferId}/complete`).then(r => r.data.data),
  cancelTransfer: (transferId: string) => apiClient.post(`/inventory/transfers/${transferId}/cancel`),

  // Audits
  getAudits: (warehouseId: string) =>
    apiClient.get<{ data: InventoryAudit[] }>(`/inventory/warehouses/${warehouseId}/audits`).then(r => r.data.data),
  createAudit: (warehouseId: string, data: { name: string; notes?: string }) =>
    apiClient.post<{ data: InventoryAudit }>(`/inventory/warehouses/${warehouseId}/audits`, data).then(r => r.data.data),
  getAudit: (auditId: string) =>
    apiClient.get<{ data: InventoryAudit }>(`/inventory/audits/${auditId}`).then(r => r.data.data),
  completeAudit: (auditId: string, items: Array<{ productId: string; countedQty: number }>) =>
    apiClient.post<{ data: InventoryAudit }>(`/inventory/audits/${auditId}/complete`, { items }).then(r => r.data.data),

  // Service consumptions
  getServiceConsumptions: (serviceId: string) =>
    apiClient
      .get<{
        data: Array<{
          id: string;
          quantity: number;
          perClient?: number;
          otherParams?: { perMl?: number; clientsCount?: number; note?: string };
          product: Product;
        }>;
      }>(`/services/${serviceId}/consumptions`)
      .then(r => r.data.data),
  setServiceConsumptions: (
    serviceId: string,
    items: Array<{
      productId: string;
      quantity: number;
      perClient?: number;
      otherParams?: { perMl?: number; clientsCount?: number; note?: string };
    }>,
  ) =>
    apiClient.put(`/services/${serviceId}/consumptions`, { items }).then(r => r.data.data),
};
